// Human-readable rendering of a session: a legible summary the coach (and Marko)
// reads in Notion, plus CSV/Markdown export. Reused by the Notion body and the
// in-app export. Pure.

import type { Exercise, Session, SetLog } from '../domain/types';
import { formatWeight } from '../domain/weight';
import { gymLabel } from '../domain/gym';

const FEEL_LABEL = { none: 'none', aware: 'aware', pain: 'PAIN' } as const;

function setLine(set: SetLog, ex: Exercise): string {
  const w = formatWeight(set.weightKg, ex);
  const rir = set.isWarmup ? 'warmup' : set.rir === 0 ? 'failure' : `${set.rir} RIR`;
  return `${set.reps} x ${w} (${rir})`;
}

// A markdown summary of one session. Legible to a human and easy for an LLM coach
// to parse alongside the JSON block.
export function sessionSummary(session: Session, exById: Map<string, Exercise>): string {
  const lines: string[] = [];
  lines.push(`# ${session.date} - ${session.routineLabel || 'Session'}`);
  // Stated up front: loads are only comparable against sessions at the same gym.
  lines.push(`Gym: ${gymLabel(session.gymId)}`);
  if (session.bodyweightKg) lines.push(`Bodyweight: ${session.bodyweightKg} kg`);
  if (session.sessionNote) lines.push(`Note: ${session.sessionNote}`);
  lines.push('');

  for (const entry of session.entries) {
    const ex = exById.get(entry.exerciseId);
    const name = ex?.name ?? entry.exerciseId;
    const sub = entry.substitutedFrom ? ` (sub for ${exById.get(entry.substitutedFrom)?.name ?? entry.substitutedFrom})` : '';
    lines.push(`## ${name}${sub}`);
    if (ex) {
      const setText = entry.sets.map((s) => setLine(s, ex)).join(', ');
      lines.push(setText);
    }
    const flags: string[] = [];
    if (entry.elbowFeel) flags.push(`elbow: ${FEEL_LABEL[entry.elbowFeel]}`);
    if (entry.forearmFeel) flags.push(`forearm: ${FEEL_LABEL[entry.forearmFeel]}`);
    if (entry.gripVariant) flags.push(`grip: ${entry.gripVariant}`);
    if (flags.length) lines.push(flags.join(' | '));
    if (entry.note) lines.push(`note: ${entry.note}`);
    lines.push('');
  }
  return lines.join('\n').trim();
}

// Flatten all sessions to CSV rows (one row per set). Total kg plus the display
// weight so the per-side convention is unambiguous in the export.
export function sessionsToCSV(sessions: Session[], exById: Map<string, Exercise>): string {
  const header = [
    'date', 'routine', 'gym', 'bodyweightKg', 'exercise', 'muscleGroup', 'substitutedFrom',
    'setNumber', 'isWarmup', 'weightTotalKg', 'weightDisplay', 'reps', 'rir', 'isFailure',
    'elbowFeel', 'forearmFeel', 'gripVariant', 'entryNote',
  ];
  const rows: string[] = [header.join(',')];
  for (const s of sessions) {
    for (const entry of s.entries) {
      const ex = exById.get(entry.exerciseId);
      for (const set of entry.sets) {
        const cells = [
          s.date,
          s.routineLabel,
          s.gymId ?? '',
          s.bodyweightKg ?? '',
          ex?.name ?? entry.exerciseId,
          ex?.muscleGroup ?? '',
          entry.substitutedFrom ?? '',
          set.setNumber,
          set.isWarmup ? 'yes' : 'no',
          set.weightKg,
          ex ? formatWeight(set.weightKg, ex) : set.weightKg,
          set.reps,
          set.rir ?? '',
          set.isFailure ? 'yes' : 'no',
          entry.elbowFeel ?? '',
          entry.forearmFeel ?? '',
          entry.gripVariant ?? '',
          entry.note ?? '',
        ];
        rows.push(cells.map(csvCell).join(','));
      }
    }
  }
  return rows.join('\n');
}

export function sessionsToMarkdown(sessions: Session[], exById: Map<string, Exercise>): string {
  return sessions.map((s) => sessionSummary(s, exById)).join('\n\n---\n\n');
}

function csvCell(v: unknown): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
